import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Loader2, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function PercentBar({ value, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div className="w-32">
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-slate-500 tabular-nums mt-0.5">{pct}%</div>
    </div>
  );
}

export default function LaporanPage() {
  const [tab, setTab] = useState("per-kelas");
  const [perKelas, setPerKelas] = useState([]);
  const [perSantri, setPerSantri] = useState([]);
  const [perTahun, setPerTahun] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kelasList, setKelasList] = useState([]);
  const [kelasFilter, setKelasFilter] = useState("all");

  const loadKelas = useCallback(async () => {
    try {
      const { data } = await api.get("/kelas", { params: { limit: 200 } });
      setKelasList(data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  const loadPerKelas = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/laporan/pembayaran/per-kelas");
      setPerKelas(data.items);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  const loadPerSantri = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (kelasFilter !== "all") params.kelas_id = kelasFilter;
      const { data } = await api.get("/laporan/pembayaran/per-santri", { params });
      setPerSantri(data.items);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [kelasFilter]);

  const loadPerTahun = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/laporan/pembayaran/per-tahun");
      setPerTahun(data.items);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadKelas(); }, [loadKelas]);
  useEffect(() => {
    if (tab === "per-kelas") loadPerKelas();
    else if (tab === "per-santri") loadPerSantri();
    else if (tab === "per-tahun") loadPerTahun();
  }, [tab, loadPerKelas, loadPerSantri, loadPerTahun]);

  return (
    <div className="space-y-6" data-testid="laporan-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Pembayaran</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Laporan Pembayaran</h1>
        <p className="text-slate-500 text-sm">Rekap pembayaran kitab per kelas, per santri, dan per tahun ajaran.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 p-1">
          <TabsTrigger value="per-kelas" data-testid="laporan-tab-kelas">Per Kelas</TabsTrigger>
          <TabsTrigger value="per-santri" data-testid="laporan-tab-santri">Per Santri</TabsTrigger>
          <TabsTrigger value="per-tahun" data-testid="laporan-tab-tahun">Per Tahun Ajaran</TabsTrigger>
        </TabsList>

        {/* Per Kelas */}
        <TabsContent value="per-kelas">
          <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kelas</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tingkatan</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Santri</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kitab</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Tagihan</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Bayar</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sisa</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Realisasi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">
                      <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                    </TableCell></TableRow>
                  ) : perKelas.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">
                      <FileSpreadsheet className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                      Belum ada data.
                    </TableCell></TableRow>
                  ) : perKelas.map((row) => (
                    <TableRow key={row.kelas_id} data-testid={`lap-kelas-row-${row.kelas_id}`} className="hover:bg-emerald-50/40">
                      <TableCell className="font-semibold text-slate-900">{row.kelas_nama}</TableCell>
                      <TableCell className="text-sm text-slate-700">{row.tingkatan || "-"}</TableCell>
                      <TableCell className="text-sm text-slate-700 tabular-nums">{row.jumlah_santri}</TableCell>
                      <TableCell className="text-sm text-slate-700 tabular-nums">{row.jumlah_kitab}</TableCell>
                      <TableCell className="text-sm font-semibold tabular-nums">{formatIDR(row.total_tagihan)}</TableCell>
                      <TableCell className="text-sm font-semibold text-emerald-700 tabular-nums">{formatIDR(row.total_pembayaran)}</TableCell>
                      <TableCell className="text-sm font-semibold text-amber-700 tabular-nums">{formatIDR(row.sisa)}</TableCell>
                      <TableCell><PercentBar value={row.total_pembayaran} total={row.total_tagihan} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* Per Santri */}
        <TabsContent value="per-santri">
          <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white mb-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">Filter Kelas:</span>
              <Select value={kelasFilter} onValueChange={setKelasFilter}>
                <SelectTrigger className="w-64 h-9" data-testid="laporan-santri-kelas-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kelas</SelectItem>
                  {kelasList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>
          <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Santri</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kelas</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kitab</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tagihan</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Bayar</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sisa</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">
                      <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                    </TableCell></TableRow>
                  ) : perSantri.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">
                      <FileSpreadsheet className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                      Belum ada data.
                    </TableCell></TableRow>
                  ) : perSantri.map((row) => (
                    <TableRow key={row.santri_id} data-testid={`lap-santri-row-${row.santri_id}`} className="hover:bg-emerald-50/40">
                      <TableCell>
                        <div className="font-semibold text-slate-900">{row.nama}</div>
                        <div className="text-xs text-slate-500 font-mono">{row.nis}</div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{row.kelas_nama}</TableCell>
                      <TableCell className="text-sm text-slate-700 tabular-nums">{row.kitab_lunas}/{row.jumlah_kitab}</TableCell>
                      <TableCell className="text-sm font-semibold tabular-nums">{formatIDR(row.total_tagihan)}</TableCell>
                      <TableCell className="text-sm font-semibold text-emerald-700 tabular-nums">{formatIDR(row.total_bayar)}</TableCell>
                      <TableCell className="text-sm font-semibold text-amber-700 tabular-nums">{formatIDR(row.sisa)}</TableCell>
                      <TableCell>
                        {row.status_lunas ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                            <CheckCircle2 className="h-3 w-3" /> Lunas
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                            <AlertCircle className="h-3 w-3" /> Belum
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* Per Tahun */}
        <TabsContent value="per-tahun">
          <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tahun Ajaran</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Jumlah Kitab</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Tagihan</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Bayar</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sisa</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Realisasi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                      <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                    </TableCell></TableRow>
                  ) : perTahun.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                      <FileSpreadsheet className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                      Belum ada data.
                    </TableCell></TableRow>
                  ) : perTahun.map((row) => (
                    <TableRow key={row.tahun_ajaran} data-testid={`lap-tahun-row-${row.tahun_ajaran}`} className="hover:bg-emerald-50/40">
                      <TableCell className="font-semibold text-slate-900">{row.tahun_ajaran}</TableCell>
                      <TableCell className="text-sm text-slate-700 tabular-nums">{row.jumlah_kitab}</TableCell>
                      <TableCell className="text-sm font-semibold tabular-nums">{formatIDR(row.total_tagihan)}</TableCell>
                      <TableCell className="text-sm font-semibold text-emerald-700 tabular-nums">{formatIDR(row.total_pembayaran)}</TableCell>
                      <TableCell className="text-sm font-semibold text-amber-700 tabular-nums">{formatIDR(row.sisa)}</TableCell>
                      <TableCell><PercentBar value={row.total_pembayaran} total={row.total_tagihan} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
